import { IconNames } from '@blueprintjs/icons';
import { formatPhoneNumber } from '@strata/helper-functions';
import { FormSectionWithMessage, FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { FIcon } from '@strata/react-status-icons';
import PropTypes from 'prop-types';

const SECTION_TITLE = 'Servicing Supplier Details';

export const ServicingSupplierDetails = ({
    supplierDetails = undefined,
    referralAtFMH,
    referralPending,
    eligibleSuppliers = undefined,
    // eslint-disable-next-line complexity
}) => {
    if (referralAtFMH) {
        const showEligibleSuppliers =
            eligibleSuppliers?.length > 0 && eligibleSuppliers?.length <= 10;
        return (
            <FormSectionWithMessage
                sectionTitle={SECTION_TITLE}
                isLoading={!eligibleSuppliers}
                icon={<FIcon />}
                primaryText="Currently in the Marketplace."
                secondaryText={
                    showEligibleSuppliers
                        ? 'One of the authorized pharmacies below will contact you to obtain the prescription.'
                        : 'An authorized pharmacy will contact you to obtain the prescription.'
                }
                additionalContent={
                    showEligibleSuppliers && (
                        <ul className="sds_supplier-details__eligible-suppliers">
                            {eligibleSuppliers?.map((supplier) => (
                                <li key={supplier.npi}>{supplier.name}</li>
                            ))}
                        </ul>
                    )
                }
            />
        );
    }

    if (referralPending) {
        return (
            <FormSectionWithMessage
                sectionTitle={SECTION_TITLE}
                isLoading={!supplierDetails}
                icon={IconNames.SHOP}
                primaryText={`Pending Acceptance at ${supplierDetails?.name}.`}
                secondaryText="Upon acceptance this authorized pharmacy will contact you to obtain the prescription."
            />
        );
    }

    return (
        <FormSectionWithRows
            sectionTitle={SECTION_TITLE}
            isLoading={!supplierDetails}
            rowOneContent={
                <>
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Servicing Supplier"
                        value={supplierDetails?.name}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Phone"
                        value={formatPhoneNumber(supplierDetails?.phone)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Fax"
                        value={formatPhoneNumber(supplierDetails?.fax)}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="Email"
                        value={<MailToHyperlink emailAddress={supplierDetails?.email} />}
                    />
                </>
            }
            rowTwoContent={
                <>
                    <AddressDetailLabelValue
                        style={{ width: '25%' }}
                        address1={supplierDetails?.address1}
                        address2={supplierDetails?.address2}
                        city={supplierDetails?.city}
                        state={supplierDetails?.state}
                        zip={supplierDetails?.zip}
                    />
                    <DetailLabelValuePair
                        style={{ width: '25%' }}
                        label="NPI"
                        value={supplierDetails?.npi}
                    />
                </>
            }
        />
    );
};

ServicingSupplierDetails.propTypes = {
    supplierDetails: PropTypes.shape({
        name: PropTypes.string,
        phone: PropTypes.string,
        fax: PropTypes.string,
        email: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
        npi: PropTypes.string,
    }),
    referralAtFMH: PropTypes.bool.isRequired,
    referralPending: PropTypes.bool.isRequired,
    eligibleSuppliers: PropTypes.arrayOf(
        PropTypes.shape({ npi: PropTypes.string, name: PropTypes.string }),
    ),
};
